import { CalendarBlank } from '@phosphor-icons/react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

// 15-min increments from 7 AM to 7 PM, formatted "1:30 PM"
export const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 7; h <= 19; h++) {
    for (let m of [0, 15, 30, 45]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const period = h < 12 ? 'AM' : 'PM';
      const h12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
      slots.push({ value: `${hh}:${mm}`, label: `${h12}:${mm} ${period}` });
    }
  }
  return slots;
})();

// Returns "HH:MM" (24h) for the next half-hour slot from `now`
export const nextHalfHourSlot = () => {
  const now = new Date();
  let hh = now.getHours();
  const mm = now.getMinutes() < 30 ? 30 : 0;
  if (mm === 0) hh = (hh + 1) % 24;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// Returns today's date as "YYYY-MM-DD" (local)
export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * DatePicker — calendar dropdown. Stores value as "YYYY-MM-DD".
 * Props: value, onChange, placeholder, testId, height ('sm'|'md')
 */
export function DatePicker({ value, onChange, placeholder = 'Pick a date', testId = 'date-picker', height = 'sm', className = '' }) {
  const h = height === 'md' ? 'h-[28px]' : 'h-[26px]';
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`w-full ${h} px-2 text-[11px] text-left border border-[#B0B0B0] mt-0.5 bg-white hover:bg-[#F5F5F5] flex items-center gap-1 ${className}`}
          data-testid={`${testId}-trigger`}
        >
          <CalendarBlank size={11} className="text-[#666]" />
          <span className="flex-1 truncate">
            {value ? format(new Date(value + 'T00:00:00'), 'MMM d, yyyy') : <span className="text-[#999]">{placeholder}</span>}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[100]" align="start" data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          selected={value ? new Date(value + 'T00:00:00') : undefined}
          onSelect={(d) => {
            if (d) {
              const yyyy = d.getFullYear();
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              onChange(`${yyyy}-${mm}-${dd}`);
            } else {
              onChange('');
            }
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * TimePicker — 15-min slot dropdown. Stores value as "HH:MM" (24h).
 */
export function TimePicker({ value, onChange, testId = 'time-picker', height = 'sm', className = '', allowEmpty = true }) {
  const h = height === 'md' ? 'h-[28px]' : 'h-[26px]';
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full ${h} px-1 text-[11px] border border-[#B0B0B0] mt-0.5 bg-white ${className}`}
      data-testid={testId}
    >
      {allowEmpty && <option value="">— No time —</option>}
      {TIME_SLOTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
    </select>
  );
}
